import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info, CalendarDays } from 'lucide-react';
import { es } from 'react-day-picker/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import CircularProgress from '@/components/common/CircularProgress';
import { bajaApi } from '../../../hooks/api/baja';
import type { BajaPaciente, BajaProgress, Paso1State, Paso2State, Paso3State, Paso5State } from '../../../hooks/api/baja';
import { StepperHeader } from './StepperHeader';
import { Paso1Motivo } from './Paso1Motivo';
import { Paso2Procesos } from './Paso2Procesos';
import { Paso3PlanTransicion } from './Paso3PlanTransicion';
import { Paso4Confirmacion } from './Paso4Confirmacion';
import { Paso5Reunion } from './Paso5Reunion';
import { activityTracker } from '@/utils/activityTracker';

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d: Date, days: number): Date {
  const x = startOfDay(d);
  x.setDate(x.getDate() + days);
  return x;
}

interface Props {
  onExit: () => void;
}

export const BajaWizard: React.FC<Props> = ({ onExit }) => {
  const [progress, setProgress] = useState<BajaProgress | null>(null);
  const [patients, setPatients] = useState<BajaPaciente[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState<'intro' | 1 | 2 | 3 | 4 | 5>('intro');
  const [fechaSalida, setFechaSalida] = useState<Date | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([bajaApi.getProgress(), bajaApi.getPatients()])
      .then(([progressRes, patientsRes]) => {
        if (cancelled) return;
        setProgress(progressRes);
        setPatients(patientsRes.results);
        if (progressRes.fechaSalidaIso) setFechaSalida(new Date(progressRes.fechaSalidaIso));
        if (progressRes.completedAt) setSent(true);
      })
      .catch(() => { if (!cancelled) setLoadError('No pudimos cargar tu proceso de baja. Recargá la página e intentá de nuevo.'); })
      .finally(() => { if (!cancelled) setLoadingPatients(false); });
    return () => { cancelled = true; };
  }, []);

  const save = (patch: Parameters<typeof bajaApi.saveProgress>[0]) => {
    bajaApi.saveProgress(patch).catch(() => { /* autosave best-effort */ });
    setProgress((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  if (loadError) {
    return (
      <div className="mx-auto max-w-md text-center space-y-4 py-16">
        <AlertTriangle className="h-8 w-8 text-coral mx-auto" />
        <p className="text-gray-700">{loadError}</p>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="flex justify-center py-24">
        <CircularProgress sizeRem={2} label="Cargando tu proceso de baja..." />
      </div>
    );
  }

  if (step === 'intro') {
    const hoy = startOfDay(new Date());
    const sugerida = addDays(hoy, 90);
    const esAnticipada = fechaSalida ? startOfDay(fechaSalida) < sugerida : false;
    const yaEmpezado = (progress.pasosCompletos?.length ?? 0) > 0;

    return (
      <div className="mx-auto w-full max-w-2xl space-y-8">
        <header className="text-center space-y-3">
          <p className="text-sm uppercase tracking-[0.25em] text-primary font-bold">Psi Mammoliti</p>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight text-gray-900">Proceso de salida</h1>
        </header>

        <aside className="rounded-2xl border border-primary/25 bg-primary/5 p-6 sm:p-7 space-y-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
              <Info className="h-4 w-4" />
            </span>
            <h2 className="text-base font-bold text-gray-900">Antes de comenzar</h2>
          </div>
          <p className="text-sm leading-relaxed text-gray-700">
            Salir del equipo Psi Mammoliti implica mucho más que una comunicación de baja. Los procesos terapéuticos
            que acompañás tienen continuidad, los pacientes que te fueron asignados tienen derechos y el equipo tiene
            la responsabilidad de asegurar que ninguno quede desatendido.
          </p>
          <p className="text-sm leading-relaxed text-gray-700">
            Por eso existe este proceso formal: planificar y declarar la transición de cada uno de tus procesos
            activos, con el tiempo y la seriedad que cada caso merece.
          </p>
          <p className="text-sm leading-relaxed text-gray-900 font-semibold">
            Cumplir con tu parte es lo que nos permite cumplir con la nuestra.
          </p>
        </aside>

        <section className="rounded-2xl border border-border bg-card p-6 sm:p-7 space-y-4">
          <div className="flex items-start gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
              <CalendarDays className="h-4 w-4" />
            </span>
            <div className="space-y-1.5">
              <label className="block text-base font-bold text-gray-900">
                ¿A partir de cuándo tenés intenciones de dejar de atender pacientes?
              </label>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Tu contrato establece un preaviso de 90 días como marco mínimo. Fecha de referencia:{' '}
                <span className="font-medium text-gray-700">{sugerida.toLocaleDateString('es-AR')}</span>.
              </p>
            </div>
          </div>

          <Calendar
            mode="single"
            selected={fechaSalida}
            onSelect={(date: Date | undefined) => setFechaSalida(date ? startOfDay(date) : undefined)}
            disabled={{ before: hoy }}
            defaultMonth={fechaSalida ?? sugerida}
            locale={es}
            className="rounded-xl border border-border p-3 bg-white"
          />

          {fechaSalida && (
            <p className="text-sm text-gray-700">
              Fecha seleccionada:{' '}
              <span className="font-semibold text-gray-900">
                {fechaSalida.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </p>
          )}

          {esAnticipada && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
              <p className="text-xs leading-relaxed text-amber-900">
                La fecha que seleccionaste es anterior al preaviso de 90 días que establece tu contrato. Cuanto menor
                sea el tiempo disponible, más importante es que la transición esté bien resuelta antes de tu salida.
              </p>
            </div>
          )}
        </section>

        <div className="flex justify-between">
          <Button type="button" variant="ghost" onClick={onExit}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!fechaSalida}
            onClick={() => {
              if (!fechaSalida) return;
              // Métricas de Actividad (funnel baja): arranca el trámite (retomar uno ya empezado no cuenta).
              if (!yaEmpezado) activityTracker.trackAction('baja_started');
              save({ fechaSalidaIso: fechaSalida.toISOString() });
              const first = progress.paso1 ? Math.max(1, (progress.pasosCompletos?.[progress.pasosCompletos.length - 1] ?? 0) + 1) : 1;
              setStep(Math.min(5, first) as 1 | 2 | 3 | 4 | 5);
            }}
            className="sm:min-w-[200px]"
          >
            {yaEmpezado ? 'Continuar' : 'Entiendo, comenzar proceso de baja'}
          </Button>
        </div>
      </div>
    );
  }

  const markComplete = (n: number) => {
    const already = progress.pasosCompletos ?? [];
    return already.includes(n) ? already : [...already, n];
  };

  return (
    <div className="space-y-10">
      <div className="mx-auto w-full max-w-4xl">
        <StepperHeader currentStep={step} />
      </div>

      {step === 1 && (
        <Paso1Motivo
          initial={progress.paso1}
          onBack={() => setStep('intro')}
          onNext={(paso1: Paso1State) => {
            const pasosCompletos = markComplete(1);
            save({ paso1, pasosCompletos });
            setStep(2);
          }}
        />
      )}

      {step === 2 && (
        <Paso2Procesos
          patients={patients}
          loadingPatients={loadingPatients}
          initial={progress.paso2}
          onBack={() => setStep(1)}
          onNext={(paso2: Paso2State) => {
            const pasosCompletos = markComplete(2);
            save({ paso2, pasosCompletos });
            setStep(3);
          }}
        />
      )}

      {step === 3 && (
        <Paso3PlanTransicion
          patients={patients}
          paso2={progress.paso2}
          initial={progress.paso3}
          onSave={(paso3: Paso3State) => save({ paso3 })}
          onBack={() => setStep(2)}
          onNext={(paso3: Paso3State) => {
            const pasosCompletos = markComplete(3);
            save({ paso3, pasosCompletos });
            setStep(4);
          }}
        />
      )}

      {step === 4 && (
        <Paso4Confirmacion
          patients={patients}
          paso1={progress.paso1}
          paso2={progress.paso2}
          paso3={progress.paso3}
          onBack={() => setStep(3)}
          onNext={() => {
            const pasosCompletos = markComplete(4);
            save({ paso4: { confirmado: true }, pasosCompletos });
            setStep(5);
          }}
        />
      )}

      {step === 5 && (
        <Paso5Reunion
          initial={progress.paso5}
          sending={sending}
          sent={sent}
          onBack={() => setStep(4)}
          onSend={(paso5: Paso5State) => {
            setSending(true);
            bajaApi
              .complete(paso5)
              .then(() => {
                // Métricas de Actividad: el trámite de baja se completó con éxito.
                activityTracker.trackAction('baja_completed');
                setSent(true);
              })
              .finally(() => setSending(false));
          }}
        />
      )}
    </div>
  );
};

export default BajaWizard;
